import React from "react";
import { Box, Flex, Stack, Text, Select, Icon, UnorderedList, ListItem, useColorModeValue } from "@chakra-ui/react";
import { MdHistory } from "react-icons/md";

/**
 * Read-only preview of a single subsection for comparison.
 * Displays subtitle, content text and optional bullet items.
 * @param {object} props
 * @param {object} props.sub - Subsection object with number, subtitle, content, items
 * @returns {React.ReactElement}
 */
function OldSubsectionPreview({ sub }) {
    const subColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
    const txtColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box pl="12px" mt="4px" borderLeft="2px solid" borderColor="whiteAlpha.200">
            <Text fontSize="0.72rem" fontWeight="600" color={subColor}>{sub.number} {sub.subtitle}</Text>
            {sub.content && (
                <Text fontSize="0.7rem" mt="2px" whiteSpace="pre-wrap" color={txtColor}>{sub.content}</Text>
            )}
            {sub.items?.length > 0 && (
                <UnorderedList m="0" pl="16px" mt="2px">
                    {sub.items.map((item, i) => (
                        <ListItem key={i} fontSize="0.68rem" color={txtColor}>{item}</ListItem>
                    ))}
                </UnorderedList>
            )}
        </Box>
    );
}

/**
 * Renders a single section card with title, text, and nested subsections.
 * @param {object} props
 * @param {object} props.section - Section object with number, title, text, subsections
 * @returns {React.ReactElement}
 */
function OldSectionCard({ section }) {
    const boxBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
    const txtColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box p="12px" mb="12px" borderRadius="8px" bg={boxBg}>
            <Text fontSize="0.78rem" fontWeight="600" color={titleColor}>{section.number}. {section.title}</Text>
            {section.text && (
                <Text fontSize="0.72rem" mt="4px" whiteSpace="pre-wrap" color={txtColor}>{section.text}</Text>
            )}
            {section.subsections?.map((sub, sIdx) => <OldSubsectionPreview key={sIdx} sub={sub} />)}
        </Box>
    );
}

/**
 * Header row with history icon, section count and version selector dropdown.
 * @param {object} props
 * @param {number} props.sectionCount - Number of sections displayed
 * @param {Array} props.versions - All available versions for the select dropdown
 * @param {string} props.selectedId - Currently selected version ID
 * @param {function} props.onVersionChange - Callback when version selection changes
 * @returns {React.ReactElement}
 */
function VersionHeader({ sectionCount, versions, selectedId, onVersionChange }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex align="center" justify="space-between" mb="12px">
            <Stack direction="row" spacing="8px" align="center">
                <Icon as={MdHistory} color={labelColor} boxSize="18px" />
                <Text fontSize="0.75rem" fontWeight="600" textTransform="uppercase" color={labelColor}>
                    Content ({sectionCount}s.)
                </Text>
            </Stack>
            {versions.length > 1 && (
                <Select size="sm" w="120px" borderRadius="8px" value={selectedId || ""}
                    onChange={(e) => onVersionChange(e.target.value)}>
                    {versions.map((v) => (
                        <option key={v.id} value={v.id}>v{v.version}{v.is_active ? " ●" : ""}</option>
                    ))}
                </Select>
            )}
        </Flex>
    );
}

/**
 * Renders version header with select dropdown and full read-only content preview.
 * Used in edit mode to compare old document content side-by-side.
 * @param {object} props
 * @param {Array} props.sections - Sections to preview
 * @param {Array} props.versions - All available versions for the select dropdown
 * @param {string} props.selectedId - Currently selected version ID
 * @param {function} props.onVersionChange - Callback when version selection changes
 * @returns {React.ReactElement}
 */
function OldContentPreview({ sections, versions, selectedId, onVersionChange }) {
    if (!sections || sections.length === 0) return null;
    return (
        <Box>
            <VersionHeader sectionCount={sections.length} versions={versions}
                selectedId={selectedId} onVersionChange={onVersionChange} />
            {sections.map((section, idx) => <OldSectionCard key={idx} section={section} />)}
        </Box>
    );
}

export default OldContentPreview;
